import React, { useState, useEffect, useRef } from "react";
import ContentPlayer from "./ContentPlayer";
import PropTypes from "prop-types";
import CourseQuiz from "../CourseQuiz/CourseQuiz";
import ShortCourseCompletedModal from "./ShortCourseCompletedModal";
import {
  buildCoursePlayerRoute,
  buildContentViewerRoute,
  learnLearningPathListView,
  libraryMyItemsMyCourses
} from "../../routes/LearningCenterRoutes";
import { ActionCableConsumer } from "react-actioncable-provider";
import { browserWSSupport } from "../../utils/webSockets";
import ContentViewerNavigationButtons from "./ContentViewerNavigationButtons";
import { useTranslation } from "react-i18next";
import CourseHomework from "./CourseHomework";
import {
  activityTypes,
  assignmentTypeIds,
  courseSectionTypes
} from "../../constants";
import ContentPlayerBottomNavbar from "./ContentPlayerBottomNavbar";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";
import { useQuizSubmission } from "../../hooks/quizSubmission";
import { fetchContentable } from "../../redux/actions/learningCenter/contentableActions";
import { enqueueErrorSnackbar } from "../../redux/actions/snackbarActions";
import ContentViewerActionButtons from "./ContentViewerActionButtons";
import FullPageLayout from "../FullPageLayout";
import ContentViewerHeader from "./ContentViewerHeader";
import { Box } from "@mui/material";
import Skeleton from "../Skeleton/Skeleton";
import PolicyPlayer from "./PolicyPlayer";
import PolicyAcknowledgement from "./PolicyAcknowledgement";

const getCourseTitle = (
  parentCourseTitle,
  contentType,
  courseName,
  courseNumber
) => {
  if (contentType !== activityTypes.policyDocument) {
    // Return parent course title for all activities other than policy document
    return parentCourseTitle;
  } else {
    // return the name of the policy version and policy number
    return `${courseName} ${courseNumber ? `| ${courseNumber}` : ""}`;
  }
};

const getSubtitle = (subtitle, contentType, manualName) => {
  // The subtitle might not be used for content types other than policy documents, so it can potentially be removed
  return contentType === activityTypes.policyDocument ? manualName : subtitle;
};

// TODO: break the quiz view out into a separate layout
const ContentViewer = props => {
  const {
    activityData,
    contentable,
    user,
    onAddToExpandedCoursesList,
    checkForCompletion,
    fetchData,
    updateQuizAnswer,
    history
  } = props;

  const {
    parentCourseId,
    assignmentTypeId,
    userAssignmentId,
    courseCompleted,
    moduleCompleted,
    lastActivity,
    parentCourseTitle,
    nextModuleItemId,
    previousId,
    nextId,
    id: activityId,
    sectionType,
    contentType,
    unfinishedResourceSurvey,
    fileType,
    downloadLink,
    releaseId,
    externalSurveyLink,
    moduleTitle,
    hasCertificate,
    nextModule,
    nextModuleDuration,
    openInNewTab,
    name: activityName,
    lastVisited,
    subtitle,
    url: activityUrl,
    settings,
    mimeType,
    courseName,
    courseNumber,
    manualName,
    contentableActiveAt,
    isEnabledAcknowledgement,
    completedAt
  } = activityData ? activityData : {};

  const { t } = useTranslation();
  const { createQuizSubmission, quizScore, isSubmitting } = useQuizSubmission();
  const dispatch = useDispatch();
  const [isUnsubmittedQuiz, setIsUnsubmittedQuiz] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState({
    showModal: false,
    exitLink: "",
    fetching: false
  });
  const [completionData, setCompletionData] = useState(null);
  const [quizCompletionModalOpen, setQuizCompletionModalOpen] = useState(false);
  let iframeRef = useRef(null);
  const [fullScreen, setFullScreen] = useState(false);
  const [iosFullScreen, setIosFullScreen] = useState(false);

  const isQuiz = contentable.quiz ? true : false;
  const isSurvey = contentable.quiz
    ? contentable.quiz.quiz_type === "survey"
    : false;
  const isPolicyDocument =
    contentType === activityTypes.policyDocument && contentable;
  const isPolicyAcknowledgement =
    contentType === activityTypes.acknowledgement && contentable;

  useEffect(() => {
    setIsUnsubmittedQuiz(isQuiz && !contentable.submitted);
  }, [contentable]);

  useEffect(() => {
    setShowCompletedModal({ ...showCompletedModal, showModal: false });
    setQuizCompletionModalOpen(false);
  }, [activityId]);

  let nextLink = null;
  const courseLink = buildCoursePlayerRoute(parentCourseId);
  if (nextId) {
    nextLink = buildContentViewerRoute(userAssignmentId, nextId);
  } else if (nextModuleItemId) {
    nextLink = buildContentViewerRoute(userAssignmentId, nextModuleItemId);
  } else {
    nextLink = courseLink;
  }

  const prevLink =
    previousId > 0 ? buildContentViewerRoute(userAssignmentId, previousId) : "";

  const handleNextActivity = () => {
    if (isUnsubmittedQuiz) {
      setShowCompletedModal({ showModal: true, exitLink: nextLink });
    } else {
      setShowCompletedModal({ ...showCompletedModal, showModal: false });
      if (props.history.location.pathname !== nextLink) {
        dispatch(push(nextLink));
      }
    }
  };

  const handleGoToInternalCourseSurvey = () => {
    if (unfinishedResourceSurvey) {
      dispatch(
        push(
          buildContentViewerRoute(userAssignmentId, unfinishedResourceSurvey)
        )
      );
    }
  };
  function handlePrevActivity() {
    if (isUnsubmittedQuiz) {
      setShowCompletedModal({ showModal: true, exitLink: prevLink });
    } else {
      dispatch(push(prevLink));
    }
  }
  const handleCompletedModalClose = () => {
    setShowCompletedModal({ ...showCompletedModal, showModal: false });
  };

  function handleCompletedModalOpen() {
    if (contentType === "scorm") {
      setShowCompletedModal({
        ...showCompletedModal,
        showModal: true,
        fetching: true
      });
      setTimeout(function() {}, 1000);
    }
    Promise.all([
      checkForCompletion(),
      fetchData(userAssignmentId, activityId)
    ]).then(data => {
      setShowCompletedModal({
        ...showCompletedModal,
        showModal: true,
        fetching: false
      });
      setCompletionData(data[0]);
    });
  }

  const handleExitToLearningPath = () => {
    const url =
      assignmentTypeId === assignmentTypeIds.selfAssigned
        ? libraryMyItemsMyCourses
        : learnLearningPathListView;
    dispatch(push(url));
    if (!(courseCompleted && moduleCompleted)) {
      onAddToExpandedCoursesList(userAssignmentId);
    }
  };

  const handleShowQuizCompletionModals = () => {
    setIsUnsubmittedQuiz(false);
    fetchData(userAssignmentId, activityId).then(() => {
      if (!isSurvey) {
        setQuizCompletionModalOpen(true);
        dispatch(fetchContentable(contentable.id));
      } else {
        // Send the learner back to the table of contents for resource surveys
        if (sectionType === courseSectionTypes.optional) {
          dispatch(push(courseLink));
        } else if (lastActivity) {
          setShowCompletedModal({ showModal: true });
        } else {
          dispatch(push(nextLink));
        }
      }
    });
  };

  const handleSubmitQuiz = () => {
    if (!isSubmitting) {
      createQuizSubmission({
        quizId: contentable.quiz.id,
        courseContentableId: contentable.id,
        userAssignmentId: userAssignmentId
      })
        .then(() => {
          handleShowQuizCompletionModals();
        })
        .catch(errors => {
          if (
            Array.isArray(errors) &&
            errors.length > 0 &&
            errors[0].title === "alreadySubmitted"
          ) {
            dispatch(
              enqueueErrorSnackbar({
                message: t(
                  `contentViewer.errorMessages.${
                    isSurvey ? "survey" : "quiz"
                  }AlreadySubmitted`
                )
              })
            );
          }
        });
    }
  };

  const navigationButtons = (
    <ContentViewerNavigationButtons
      lastActivity={lastActivity}
      previousLink={prevLink}
      shortCourseCompleted={moduleCompleted}
      onExitShortCourse={handleCompletedModalOpen}
      handleNextActivity={handleNextActivity}
      handlePrevActivity={handlePrevActivity}
      showSubmitQuizButton={isUnsubmittedQuiz}
      disabled={!activityData}
    />
  );

  const actionButtons = (
    <ContentViewerActionButtons
      fullScreen={fullScreen}
      onFullScreenChange={setFullScreen}
      allowFullScreen={
        (iframeRef && iframeRef.current) || contentType === "video"
      }
      downloadLink={downloadLink}
      contentType={contentType}
      fileType={fileType}
    />
  );

  const skeletonLoader = (
    <Box px={7} height="100%" width="100%">
      <Skeleton width="100%" height="100%" loading maxHeight={500} />
    </Box>
  );

  const contentViewerContent = (
    <>
      {!!parentCourseId && !!browserWSSupport && (
        <>
          <ActionCableConsumer
            channel={{
              channel: "CompletionsChannel",
              course_id: parentCourseId,
              user_id: user.id
            }}
            onReceived={response => {
              console.log(response);
              checkForCompletion();
            }}
          />
          {!!activityId && (
            <ActionCableConsumer
              channel={{
                channel: "ActivityChannel",
                activity_id: activityId
              }}
              onReceived={response => {
                if (response.status === "progress_refreshed") {
                  alert(t("coursePlayer.courseActivityUpdatedMessage"));
                  history.push(`/learning-center/courses/${releaseId}`);
                }
              }}
            />
          )}
        </>
      )}
      {(lastActivity || isUnsubmittedQuiz || moduleCompleted) &&
        showCompletedModal.showModal && (
          <ShortCourseCompletedModal
            externalSurveyLink={externalSurveyLink}
            showDialog={showCompletedModal.showModal}
            moduleTitle={moduleTitle}
            nextModule={nextModule}
            nextModuleDuration={nextModuleDuration}
            moduleCompleted={moduleCompleted}
            courseTitle={parentCourseTitle}
            courseId={parentCourseId}
            courseCompleted={courseCompleted}
            assignmentTypeId={assignmentTypeId}
            isUnsubmittedQuiz={isUnsubmittedQuiz}
            survey={isSurvey}
            onNextActivity={() => handleNextActivity()}
            unfinishedResourceSurvey={unfinishedResourceSurvey}
            onInternalCourseSurvey={() => handleGoToInternalCourseSurvey()}
            close={handleCompletedModalClose}
            exitLink={isUnsubmittedQuiz ? showCompletedModal.exitLink : null}
            onExitToLearningPath={handleExitToLearningPath}
            hasCertificate={hasCertificate}
            recordsScore={!!completionData && completionData.records_score}
            isCoursePassed={!!completionData && completionData.passed}
            fetchingConmpletions={showCompletedModal.fetching}
          />
        )}
      {!activityData ? (
        skeletonLoader
      ) : isQuiz ? (
        <CourseQuiz
          contentableId={contentable.id}
          submitted={!isUnsubmittedQuiz}
          quiz={contentable.quiz}
          questions={contentable.questions}
          learnerAnswers={contentable.learnerAnswers}
          questionsPerPage={contentable.questionsPerPage}
          updateQuizAnswer={updateQuizAnswer}
          onSubmitQuiz={handleSubmitQuiz}
          userAssignmentId={userAssignmentId}
          survey={isSurvey}
          showModal={handleCompletedModalOpen}
          navigationButtons={navigationButtons}
          isSubmitting={isSubmitting}
          quizCompletionModalOpen={quizCompletionModalOpen}
          onCloseQuizCompletionModal={() => setQuizCompletionModalOpen(false)}
          quizScore={quizScore}
          onNextActivity={handleNextActivity}
          onExitShortCourse={handleCompletedModalOpen}
          lastActivity={lastActivity}
          moduleCompleted={moduleCompleted}
          skeletonLoader={skeletonLoader}
        />
      ) : contentType === activityTypes.homework && contentable ? (
        <CourseHomework
          homework={contentable.extendable}
          contentableId={contentable.id}
          userAssignmentId={userAssignmentId}
          skeletonLoader={skeletonLoader}
        />
      ) : isPolicyDocument ? (
        <PolicyPlayer
          content={contentable.extendable?.content}
          policyVersionTitle={courseName}
          policyNumber={courseNumber}
          manualTitle={manualName}
          lastUpdatedDate={contentableActiveAt}
          navigationButtons={navigationButtons}
          trainingObjectId={parentCourseId}
          courseContentableId={activityId}
          isComplete={!!completedAt}
          isSelfAssigned={assignmentTypeId === assignmentTypeIds.selfAssigned}
        />
      ) : isPolicyAcknowledgement ? (
        <PolicyAcknowledgement
          textBody={contentable.extendable?.textBody}
          isComplete={!!completedAt}
          alowSubmit={isEnabledAcknowledgement}
          trainingObjectId={parentCourseId}
          courseContentableId={activityId}
          onSubmitSuccess={handleCompletedModalOpen}
        />
      ) : (
        <ContentPlayer
          contentLink={activityUrl}
          openInNewTab={openInNewTab}
          contentSettings={settings}
          downloadLink={downloadLink}
          contentType={contentType}
          mimeType={mimeType}
          fileType={fileType}
          onFullScreenChange={setFullScreen}
          fullScreen={fullScreen}
          iframeRef={iframeRef}
          iosFullScreen={iosFullScreen}
          onIosFullScreenChange={setIosFullScreen}
          exitModalOpen={showCompletedModal.showModal}
          fetchingConmpletion={showCompletedModal.fetching}
        />
      )}
      {!isQuiz && !isPolicyDocument && !isPolicyAcknowledgement && (
        <ContentPlayerBottomNavbar navButtons={navigationButtons} />
      )}
    </>
  );

  return (
    <FullPageLayout
      title={
        <Box height={40}>
          {getCourseTitle(
            parentCourseTitle,
            contentType,
            courseName,
            courseNumber
          )}
        </Box>
      }
    >
      <ContentViewerHeader
        activityTitle={activityName}
        contentType={contentType}
        lastVisited={lastVisited}
        subtitle={getSubtitle(subtitle, contentType, manualName)}
        navigationButtons={navigationButtons}
        isLoading={!activityData}
      />
      <Box mt={1}>{actionButtons}</Box>
      <Box mb={10} mt={3} flex={1}>
        {contentViewerContent}
      </Box>
    </FullPageLayout>
  );
};

export default ContentViewer;

ContentViewer.propTypes = {
  courseType: PropTypes.string,
  lastActivity: PropTypes.bool,
  contentType: PropTypes.string,
  fileType: PropTypes.string,
  lastVisited: PropTypes.string,
  activityTitle: PropTypes.string,
  nextModule: PropTypes.string,
  nextModuleDuration: PropTypes.number,
  userAssignmentId: PropTypes.number,
  moduleCompleted: PropTypes.bool,
  url: PropTypes.string,
  openInNewTab: PropTypes.bool,
  downloadLink: PropTypes.string,
  courseTitle: PropTypes.string,
  courseCompleted: PropTypes.bool,
  externalSurveyLink: PropTypes.string,
  survey: PropTypes.bool,
  unfinishedResourceSurvey: PropTypes.number,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  moduleTitle: PropTypes.string,
  quiz: PropTypes.any,
  onSubmitQuiz: PropTypes.func,
  checkForCompletion: PropTypes.func
};
